import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useRef, useState } from 'react';
import GlassCard from '../../components/GlassCard';
import WorkProps from '../../model/work-props';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import WebIcon from '@mui/icons-material/Web';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

// Category definitions
const categories = [
  { id: 'mobile', label: 'Mobile Apps', icon: PhoneIphoneIcon },
  { id: 'web', label: 'Web Applications', icon: WebIcon },
  { id: 'automation', label: 'Automated Systems', icon: PrecisionManufacturingIcon },
];

// Mobile Projects
const mobileProjects = [
  {
    id: 1,
    name: 'Mindamigo',
    company: 'Solitera Software',
    description: 'Mental well-being application featuring guided meditation sessions, mood tracking, and personalized wellness journeys. Led development managing project architecture and team of mobile/backend engineers.',
    tech: ['Flutter', 'Firebase', 'Node.js', 'REST APIs'],
    highlights: ['Team Leadership', 'Full-stack Development', 'User Engagement'],
  },
  {
    id: 2,
    name: 'MatchStixx',
    company: 'Solitera Software',
    description: 'Social gaming platform connecting players through real-time matching algorithms. Designed database architecture and built RESTful APIs for seamless player interactions and team formations.',
    tech: ['Flutter', 'Node.js', 'MongoDB', 'REST APIs', 'WebSocket'],
    highlights: ['Real-time Matching', 'Social Features', 'Scalable Backend'],
  },
];

// Web Application Projects
const webProjects = [
  {
    id: 3,
    name: 'Timesheet Management',
    company: 'Solitera Software',
    description: 'Enterprise-grade system for tracking employee project hours, improving resource allocation, billing accuracy, and project management across distributed teams.',
    tech: ['Node.js', 'HTML/CSS', 'MySQL', 'REST APIs'],
    highlights: ['Resource Planning', 'Billing Integration', 'Analytics Dashboard'],
  },
  {
    id: 4,
    name: 'Test Management System',
    company: 'Solitera Software',
    description: 'Comprehensive platform for managing test cases, test suites, and execution tracking. Streamlined QA workflows with detailed reporting and traceability.',
    tech: ['Node.js', 'React.js', 'PostgreSQL', 'REST APIs'],
    highlights: ['Test Tracking', 'Coverage Reports', 'CI/CD Integration'],
  },
  {
    id: 5,
    name: 'Test Automation Framework',
    company: 'Solitera Software',
    description: 'Scalable automation framework supporting multiple testing types including UI, API, and performance testing with parallel execution capabilities.',
    tech: ['Selenium', 'Node.js', 'Jenkins', 'Docker'],
    highlights: ['Parallel Execution', 'Cross-browser', 'CI/CD Ready'],
  },
  {
    id: 6,
    name: 'Test Estimation Tool',
    company: 'Solitera Software',
    description: 'Intelligent estimation system using historical data and complexity analysis to provide accurate QA effort predictions for project planning.',
    tech: ['Node.js', 'React.js', 'Machine Learning', 'PostgreSQL'],
    highlights: ['AI-powered', 'Historical Analysis', 'Accurate Predictions'],
  },
];

// Automated Systems - Crosslink Microservices
const crosslinkMicroservices = [
  {
    id: 'scheduler',
    name: 'Scheduler',
    description: 'Workflow orchestration engine managing task queues, job scheduling, and distributed workflow execution across the platform.',
    icon: '⏱️',
  },
  {
    id: 'core',
    name: 'Core',
    description: 'Centralized configuration hub containing all system info, settings, and configurations required by microservices.',
    icon: '🎯',
  },
  {
    id: 'liquid',
    name: 'Liquid.js Engine',
    description: 'Template processing service transforming unstructured telecom data into standardized, structured formats.',
    icon: '🔄',
  },
  {
    id: 'taskfactory',
    name: 'Task Factory',
    description: 'Deferred execution service managing scheduled tasks, retries, and future job processing.',
    icon: '🏭',
  },
  {
    id: 'anomaly',
    name: 'Anomaly Detection',
    description: 'Real-time monitoring system identifying network inconsistencies and alerting RF engineers instantly.',
    icon: '🔍',
  },
  {
    id: 'controller',
    name: 'Controller',
    description: 'Central automation brain handling data parsing, transformation logic, and orchestrating service interactions.',
    icon: '🧠',
  },
  {
    id: 'connect',
    name: 'Connect',
    description: 'Multi-protocol integration service automating CLI, SSH, SFTP, FTP, and API communications.',
    icon: '🔌',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
  exit: {
    opacity: 0,
    transition: { staggerChildren: 0.05, staggerDirection: -1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
  },
};

// Project Card Component
function ProjectCard({ project, isMobile }: { project: typeof mobileProjects[0]; isMobile?: boolean }) {
  return (
    <GlassCard
      enableTilt={!isMobile}
      glowColor="rgba(68, 212, 130, 0.15)"
      sx={{
        height: '100%',
        minHeight: '320px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Company badge */}
      <Typography
        sx={{
          fontFamily: 'Fira Code, monospace',
          fontSize: '0.65rem',
          color: '#44D482',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          mb: 1,
        }}
      >
        {project.company}
      </Typography>

      {/* Title */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: '#fff',
          mb: 2,
        }}
      >
        {project.name}
      </Typography>

      {/* Description */}
      <Typography
        sx={{
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '0.9rem',
          lineHeight: 1.7,
          flex: 1,
          mb: 3,
        }}
      >
        {project.description}
      </Typography>

      {/* Highlights */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {project.highlights.map((highlight) => (
          <Chip
            key={highlight}
            label={highlight}
            size="small"
            sx={{
              background: 'rgba(68, 212, 130, 0.1)',
              color: '#44D482',
              fontSize: '0.7rem',
              height: 24,
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
        ))}
      </Box>

      {/* Tech Stack */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 'auto' }}>
        {project.tech.map((tech) => (
          <Typography
            key={tech}
            sx={{
              fontFamily: 'Fira Code, monospace',
              fontSize: '0.65rem',
              color: 'rgba(255, 255, 255, 0.5)',
              padding: '3px 8px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '4px',
            }}
          >
            {tech}
          </Typography>
        ))}
      </Box>
    </GlassCard>
  );
}

// Crosslink Architecture Component
function CrosslinkArchitecture() {
  return (
    <Box>
      {/* Crosslink Header */}
      <GlassCard
        sx={{
          mb: 4,
          background: 'linear-gradient(135deg, rgba(68, 212, 130, 0.15) 0%, rgba(68, 212, 130, 0.05) 100%)',
          border: '1px solid rgba(68, 212, 130, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 70,
              height: 70,
              background: 'linear-gradient(135deg, #44D482 0%, #2A9D5C 100%)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <PrecisionManufacturingIcon sx={{ fontSize: 36, color: '#0A0A0F' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: 'Fira Code, monospace',
                fontSize: '0.7rem',
                color: '#44D482',
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                mb: 0.5,
              }}
            >
              Amdocs • Enterprise Automation
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'Bebas Neue, sans-serif',
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: '#fff',
                mb: 1,
              }}
            >
              CROSSLINK RPA PLATFORM
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.95rem',
                lineHeight: 1.7,
                maxWidth: '700px',
              }}
            >
              Enterprise-grade automation solution for telecom configuration documents, reducing processing time by{' '}
              <strong style={{ color: '#44D482' }}>99%</strong> (from 3-4 hours to under 1 minute). 
              Processing <strong style={{ color: '#44D482' }}>100,000+</strong> network configurations daily with{' '}
              <strong style={{ color: '#44D482' }}>99.9%</strong> reliability.
            </Typography>
          </Box>
        </Box>

        {/* Tech Stack */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3, pt: 3, borderTop: '1px solid rgba(68, 212, 130, 0.15)' }}>
          {['Python', 'FastAPI', 'Node.js', 'Nest.js', 'React.js', 'Liquid.js', 'Redis', 'PostgreSQL', 'Docker', 'Kubernetes'].map((tech) => (
            <Typography
              key={tech}
              sx={{
                fontFamily: 'Fira Code, monospace',
                fontSize: '0.7rem',
                color: '#44D482',
                padding: '4px 12px',
                background: 'rgba(68, 212, 130, 0.1)',
                borderRadius: '6px',
                border: '1px solid rgba(68, 212, 130, 0.2)',
              }}
            >
              {tech}
            </Typography>
          ))}
        </Box>
      </GlassCard>

      {/* Microservices Architecture */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <AccountTreeIcon sx={{ color: '#44D482', fontSize: 24 }} />
          <Typography
            sx={{
              fontFamily: 'Fira Code, monospace',
              fontSize: '0.85rem',
              color: 'rgba(255, 255, 255, 0.7)',
              letterSpacing: '0.05em',
            }}
          >
            Microservices Architecture
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {crosslinkMicroservices.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={service.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <Box
                  sx={{
                    background: 'rgba(255, 255, 255, 0.02)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(68, 212, 130, 0.15)',
                    borderRadius: '12px',
                    padding: 2.5,
                    height: '100%',
                    minHeight: '160px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(68, 212, 130, 0.05)',
                      border: '1px solid rgba(68, 212, 130, 0.3)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>{service.icon}</Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: '#fff',
                      fontSize: '0.95rem',
                      mb: 1,
                    }}
                  >
                    {service.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontSize: '0.8rem',
                      lineHeight: 1.6,
                    }}
                  >
                    {service.description}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}

export default function Works({ isMobile }: WorkProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [activeCategory, setActiveCategory] = useState('mobile');

  const renderContent = () => {
    switch (activeCategory) {
      case 'mobile':
        return (
          <Grid container spacing={3}>
            {mobileProjects.map((project) => (
              <Grid item xs={12} md={6} key={project.id}>
                <motion.div variants={itemVariants}>
                  <ProjectCard project={project} isMobile={isMobile} />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        );
      case 'web':
        return (
          <Grid container spacing={3}>
            {webProjects.map((project) => (
              <Grid item xs={12} sm={6} lg={3} key={project.id}>
                <motion.div variants={itemVariants}>
                  <ProjectCard project={project} isMobile={isMobile} />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        );
      case 'automation':
        return <CrosslinkArchitecture />;
      default:
        return null;
    }
  };

  return (
    <Box
      component="section"
      id="works"
      ref={ref}
      sx={{
        py: { xs: 10, md: 15 },
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <Typography
            sx={{
              fontFamily: 'Fira Code, monospace',
              fontSize: '0.85rem',
              color: '#44D482',
              letterSpacing: '0.1em',
              mb: 2,
            }}
          >
            {'// PORTFOLIO'}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: { xs: '2.5rem', md: '4rem' },
              color: '#fff',
              mb: 2,
            }}
          >
            FEATURED WORKS
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '1rem',
              maxWidth: '600px',
              mb: 5,
            }}
          >
            From mobile applications to enterprise automation systems — crafting solutions that make an impact.
          </Typography>
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, md: 2 },
              mb: 5,
              flexWrap: 'wrap',
            }}
          >
            {categories.map((category) => {
              const Icon = category.icon;
              const isActive = activeCategory === category.id;
              return (
                <Box
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    padding: { xs: '10px 16px', md: '12px 24px' },
                    background: isActive ? 'rgba(68, 212, 130, 0.15)' : 'rgba(255, 255, 255, 0.03)',
                    border: isActive ? '1px solid rgba(68, 212, 130, 0.4)' : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: isActive ? 'rgba(68, 212, 130, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(68, 212, 130, 0.3)',
                    },
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: { xs: 18, md: 22 },
                      color: isActive ? '#44D482' : 'rgba(255, 255, 255, 0.5)',
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: 'Space Grotesk, sans-serif',
                      fontSize: { xs: '0.85rem', md: '0.95rem' },
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? '#44D482' : 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    {category.label}
                  </Typography>
                  {category.id === 'automation' && (
                    <Box
                      sx={{
                        background: '#44D482',
                        color: '#0A0A0F',
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontFamily: 'Fira Code, monospace',
                      }}
                    >
                      FEATURED
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </motion.div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </Container>
    </Box>
  );
}
